import { useTranslation } from "react-i18next";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";
import { buildCreateTarifDefaultValues, CreateTarifFormData, CreateTarifSchema } from "./tarifForm";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCreateTarif } from "@/api/tarif.api";
import { Form } from "@/components/ui/form";
import { ControlledCheckbox } from "@/components/ui/customs/controlled-checkbox";
import { Loader2 } from "lucide-react";
import { TarifReducedDto } from "@/types/api/api-types";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { useEffect, useMemo } from "react";

interface TarifCreateModalProps {
  open: boolean;
  closeModal: () => void;
  tarifs: TarifReducedDto[];
}

const FORM_ID = "create-tarif-form";

export const TarifCreateModal = ({ open, closeModal, tarifs }: TarifCreateModalProps) => {
  const { t } = useTranslation(["boParameters", "common"]);
  const { mutateAsync: createTarif, isPending } = useCreateTarif();

  const defaultTarifId = useMemo(() => tarifs.find((tarif) => tarif.isDefault)?.id ?? "", [tarifs]);

  const copyOptions = useMemo(
    () =>
      tarifs
        .filter((tarif) => !tarif.followingId && !tarif.isExpress)
        .map((tarif) => ({ label: tarif.label, value: tarif.id })),
    [tarifs],
  );

  const form = useForm<CreateTarifFormData>({
    resolver: zodResolver(CreateTarifSchema),
    defaultValues: buildCreateTarifDefaultValues(defaultTarifId),
  });

  useEffect(() => {
    if (open) {
      form.reset(buildCreateTarifDefaultValues(defaultTarifId));
    }
  }, [open, defaultTarifId, form]);

  const tarifIdToCopy = useWatch({ control: form.control, name: "tarifIdToCopy" });

  useEffect(() => {
    if (!tarifIdToCopy) {
      form.setValue("changePercentage", 0);
    }
  }, [tarifIdToCopy, form]);

  const onSubmit = async (data: CreateTarifFormData) => {
    if (isPending) return;
    await createTarif({ ...data, changePercentage: data.changePercentage ?? 0 });
    form.reset();
    closeModal();
  };

  return (
    <Modal
      open={open}
      onClose={closeModal}
      title={t("tarifCreateModal.title")}
      size="sm"
      footer={
        <div className="flex w-full flex-col gap-2">
          <Button type="submit" form={FORM_ID} disabled={isPending} className="w-full">
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t("tarifCreateModal.submit")
            )}
          </Button>
          <Button variant="outline" onClick={closeModal} className="w-full">
            {t("common:actions.cancel")}
          </Button>
        </div>
      }
    >
      <Form {...form}>
        <form
          id={FORM_ID}
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex w-full flex-col gap-4"
        >
          <ControlledUnifiedInput
            name="label"
            control={form.control}
            label={t("tarifCreateModal.labelLabel")}
            placeholder={t("tarifCreateModal.labelPlaceholder")}
          />

          <ControlledSelectV2
            name="tarifIdToCopy"
            control={form.control}
            label={t("tarifCreateModal.tarifToCopyLabel")}
            placeholder={t("tarifCreateModal.tarifToCopyPlaceholder")}
            options={copyOptions}
            contentClassName="w-full"
            clearable
          />
          <ControlledUnifiedInput
            name="changePercentage"
            control={form.control}
            label={t("tarifCreateModal.changePercentageLabel")}
            placeholder={t("tarifCreateModal.changePercentagePlaceholder")}
            inputType="integer"
            disabled={!tarifIdToCopy}
          />
          <ControlledCheckbox
            name="shouldFollowTarif"
            control={form.control}
            label={t("tarifCreateModal.followTarifLabel")}
          />
        </form>
      </Form>
    </Modal>
  );
};
